/**
 * /app/renata/push — Push Campaign Manager.
 * Renata proposes → Approval required → OneSignal send.
 */
import { createFileRoute } from "@tanstack/react-router";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { PUSH_STATUS_ES, labelStatus } from "@/lib/renata/experience/status-labels";
import { useEffect, useState } from "react";
import {
  Bell,
  Plus,
  Loader2,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { PushSegment } from "@/lib/renata/channels/push";
import { validatePushDraft } from "@/lib/renata/channels/push";

export const Route = createFileRoute("/app/renata/push")({
  component: RenataPushPage,
  head: () => ({ meta: [{ title: "Renata — Avisos push" }] }),
});

interface Campaign {
  id: string;
  title: string;
  body: string;
  segment: PushSegment;
  status: string;
  preview: { estimatedReach: number | null; segmentLabel: string; warningMsg?: string };
  created_at: string;
  sent_at: string | null;
  stats: { sent?: number; opened?: number };
}

const SEGMENT_OPTIONS: { value: PushSegment; label: string }[] = [
  { value: "all", label: "Todos los suscriptores" },
  { value: "recurring", label: "Clientes frecuentes" },
  { value: "lapsed", label: "Inactivos 30+ días" },
  { value: "vip", label: "Clientes VIP" },
  { value: "inactive", label: "Sin compras recientes" },
  { value: "pwa_installed", label: "PWA instalada (mejor esfuerzo)" },
];

const STATUS_CONFIG: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  draft: { label: "Borrador", icon: Clock, className: "bg-muted text-muted-foreground" },
  pending_approval: { label: "Pendiente", icon: AlertTriangle, className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" },
  approved: { label: "Aprobada", icon: CheckCircle2, className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  sent: { label: "Enviada", icon: Send, className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  cancelled: { label: "Cancelada", icon: XCircle, className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium", cfg.className)}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function RenataPushPage() {
  const tenantId = useWorkspaceTenantId();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ title: "", body: "", segment: "all" as PushSegment });

  async function load() {
    if (!tenantId) return;
    try {
      const res = await fetch("/api/renata/push", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      const data = (await res.json()) as { campaigns: Campaign[] };
      setCampaigns(data.campaigns ?? []);
    } catch {
      toast.error("No se pudieron cargar las campañas");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [tenantId]);

  async function handleCreate() {
    if (!tenantId) return;
    const errors = validatePushDraft(draft);
    if (errors.length > 0) {
      toast.error(errors.join(" "));
      return;
    }
    setCreating(true);
    try {
      const res = await fetch("/api/renata/push", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Error al crear campaña");
      }
      toast.success("Campaña creada como borrador. Revisá y aprobá antes de enviar.");
      setShowForm(false);
      setDraft({ title: "", body: "", segment: "all" });
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear campaña");
    } finally {
      setCreating(false);
    }
  }

  if (!tenantId) return null;

  return (
    <div className="flex min-w-0 flex-col p-4 md:p-6 space-y-4">
      <RenataPageChrome
        screenId="push"
        title="Avisos push"
        subtitle="Enviá una notificación a tus clientes. Antes de enviar revisás audiencia, mensaje, canal y alcance estimado."
        showBack
        breadcrumb={["Trabajo", "Avisos push"]}
      >
        <div className="flex justify-end">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Nueva campaña
          </Button>
        </div>
        <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            Los avisos van sólo a suscriptores con la app instalada y permisos activos.
            Siempre requieren tu aprobación — Renata nunca envía sola.
          </p>
        </div>
      </RenataPageChrome>

      <div className="flex-1">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && campaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center max-w-md mx-auto">
            <Bell className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">Todavía no enviaste avisos push</p>
            <p className="text-xs text-muted-foreground">
              Creá un borrador con audiencia y mensaje. Renata lo prepara; vos aprobás antes de enviar.
            </p>
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
              Crear primera campaña
            </Button>
          </div>
        )}

        {!loading && campaigns.length > 0 && (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">{c.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.body}</p>
                  </div>
                  <StatusBadge status={labelStatus(PUSH_STATUS_ES, c.status)} />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="rounded-full bg-muted px-2 py-0.5">
                    {c.preview?.segmentLabel ?? c.segment}
                  </span>
                  <span>
                    Alcance:{" "}
                    {c.preview?.estimatedReach == null
                      ? "no disponible todavía"
                      : c.preview.estimatedReach}
                  </span>
                  {c.stats?.sent != null && (
                    <span>Enviados: {c.stats.sent}</span>
                  )}
                  {c.stats?.opened != null && (
                    <span>Abiertos: {c.stats.opened}</span>
                  )}
                  {c.preview?.warningMsg && (
                    <span className="text-yellow-600 dark:text-yellow-400">
                      ⚠ {c.preview.warningMsg}
                    </span>
                  )}
                  <span className="ml-auto">
                    {new Date(c.created_at).toLocaleDateString("es-AR")}
                  </span>
                </div>
                {c.status === "draft" && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Enviá esta campaña a Renata para revisión: mencionala en el chat para generar una propuesta de aprobación.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create campaign dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-violet-500" />
              Nueva campaña push
            </DialogTitle>
          <DialogDescription className="sr-only">Formulario de aviso push. Completá título, mensaje y segmento.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="push-title" className="text-xs font-medium">
                Título <span className="text-muted-foreground">({draft.title.length}/64 recomendados)</span>
              </Label>
              <Input
                id="push-title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="Ej: ¡Oferta de fin de semana!"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="push-body" className="text-xs font-medium">
                Mensaje <span className="text-muted-foreground">({draft.body.length}/180 recomendados)</span>
              </Label>
              <Textarea
                id="push-body"
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                placeholder="Ej: Descuento 20% en toda la tienda. ¡Sólo hoy!"
                className="mt-1 min-h-[80px] resize-none"
              />
            </div>

            <div>
              <Label htmlFor="push-segment" className="text-xs font-medium">Segmento</Label>
              <Select
                value={draft.segment}
                onValueChange={(v) => setDraft((d) => ({ ...d, segment: v as PushSegment }))}
              >
                <SelectTrigger id="push-segment" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-sm">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Se guarda como borrador. Para enviar, necesitás aprobar la propuesta en el chat de Renata.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => void handleCreate()}
              disabled={creating}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar borrador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
